import { MaxHeap } from '@datastructures-js/heap';
import {
  EventSeriesOption,
  TimeChartSeriesMapping,
  TimeScale,
  TimeSeriesDataset,
  TimeSeriesDatasetSource,
  TimeSeriesOption,
} from '@/core';
import { ChartApi } from '../EChart';
import { CursorCoordinates, CursorData } from '../PanelTooltip';
import { checkDatasetForClosestTimestamp, getPointInGrid } from '../utils';
import {
  EMPTY_TOOLTIP_DATA,
  ExcludeEmptyResults,
  TooltipConfig,
  TooltipSeriesArray,
  TooltipSeriesInfo,
} from './tooltip-model';

export interface CheckForNearbyEventProps {
  pointInGrid: number[];
  xInterval: number;
  eventSeries?: TimeSeriesOption;
}

export const LOG_SCALE_AXIS_NEAR_ZERO_NUMBER = Number.MIN_VALUE;

/**
 * Given the coordinates of the mouse click (pointInGrid), find the nearest event
 */
export function checkForNearestEvent(props: CheckForNearbyEventProps) {
  const { eventSeries, pointInGrid, xInterval } = props;
  const eventCursorX = pointInGrid[0] ?? null;
  const eventsData = eventSeries?.data;

  // return if there is no event or if there is not an x-coordinate of the mouse click
  if (eventsData === undefined || eventCursorX === null) return EMPTY_TOOLTIP_DATA;

  // find the event that is nearest to the mouse click by looping through all events
  let closestTimestamp: number | null = null;
  let minDistance = Infinity;
  let nearestEvent: EventSeriesOption | null = null;

  for (let i = 0; i < eventsData.length; i++) {
    const event = eventsData[i] as EventSeriesOption;
    if (event.value) {
      const eventTimestamp = event.value[0];
      const distance = Math.abs(eventTimestamp - eventCursorX);
      if (distance < minDistance) {
        closestTimestamp = eventTimestamp;
        minDistance = distance;
        nearestEvent = event;
      }
    }
  }

  // Adjust xBuffer to increase or decrease the time range at which the tooltip is displayed
  const xBuffer = xInterval / 4;

  const isClickWithinRange =
    closestTimestamp !== null &&
    eventCursorX <= closestTimestamp + xBuffer &&
    eventCursorX >= closestTimestamp - xBuffer;

  // If the mouse click falls within the range at which the tooltip should appear, return the nearest event
  if (nearestEvent && closestTimestamp && isClickWithinRange) {
    return [
      {
        seriesIndex: -1,
        dataIndex: null,
        seriesName: nearestEvent?.event?.title ?? '',
        date: closestTimestamp,
        x: nearestEvent.value[0],
        y: nearestEvent.value[1],
        formattedY: '',
        markerColor: 'blue',
        shouldEmphasizeText: false,
        distanceFromCursorY: 0, // TODO: make optional for events
        event: nearestEvent.event,
        seriesId: '',
      },
    ] as TooltipSeriesArray;
  }

  return EMPTY_TOOLTIP_DATA;
}

export interface CheckForNearbySeriesProps {
  maxNearbySeries: number;
  dataset: TimeSeriesDataset[];
  seriesMapping: TimeChartSeriesMapping;
  cursorY: number | number[];
  yBuffer: number | number[];
  closestTimestamp: number;
  closestTimestampIndex: number;
  eventSeries?: TimeSeriesOption;
  mouseoverSeriesId?: string;
  excludeEmptyResults?: ExcludeEmptyResults;
  getSeriesById?: TooltipConfig['getSeriesById'];
}

/**
 * Returns formatted series data for the points that are close to the user's cursor.
 * Adjust xBuffer and yBuffer to increase or decrease number of series shown.
 */
export function checkforNearbyTimeSeries(props: CheckForNearbySeriesProps): TooltipSeriesArray {
  const {
    maxNearbySeries,
    dataset,
    seriesMapping,
    cursorY: maybeArrayCursorY,
    yBuffer: maybeArrayYBuffer,
    closestTimestamp,
    closestTimestampIndex,
    mouseoverSeriesId,
    excludeEmptyResults,
    getSeriesById,
  } = props;

  const cursorY = Array.isArray(maybeArrayCursorY) ? maybeArrayCursorY : [maybeArrayCursorY];
  const yBuffer = Array.isArray(maybeArrayYBuffer) ? maybeArrayYBuffer : [maybeArrayYBuffer];

  if (cursorY === null || !Array.isArray(dataset)) return EMPTY_TOOLTIP_DATA;

  // Initialize a MaxHeap (aka priority queue) to efficiently manage the closest series using a comparison function.
  // A MaxHeap is a binary tree where the parent node is always greater than or equal to its child nodes.
  // We compare using distanceFromCursorY to ensure the farthest series is replaced when a closer series is found.
  // This approach has a complexity of O(totalSeries log maxNearbySeries), more efficient than a sorted array,
  // which has a complexity of O(totalSeries * maxNearbySeries) due to shifting elements on each insertion.
  // More details: https://github.com/datastructures-js/heap?tab=readme-ov-file#minheap-maxheap
  const nearbySeriesHeap = new MaxHeap<TooltipSeriesInfo>((seriesInfo) => seriesInfo.distanceFromCursorY ?? Infinity);

  // Use seriesMapping length since dataset stays stable when selected legend items change
  const totalSeries = seriesMapping.length;

  let estimatedClosestSeries: TooltipSeriesInfo | undefined;

  // If ECharts mouseover event in TimeChartBase finds a series, lookup the info to show in tooltip.
  const mouseoverSeriesInfo = getSeriesById && mouseoverSeriesId ? getSeriesById(mouseoverSeriesId) : undefined;
  if (mouseoverSeriesInfo) {
    const mouseoverSeries = seriesMapping[mouseoverSeriesInfo.mappingIndex];
    if (mouseoverSeries?.datasetIndex !== undefined) {
      const mouseoverValues = dataset[mouseoverSeries.datasetIndex]?.source[closestTimestampIndex];
      if (mouseoverValues) {
        const [xValue, yValue] = mouseoverValues;
        if (xValue !== null && yValue !== null) {
          estimatedClosestSeries = getSeriesInfo({
            seriesIndex: mouseoverSeries.datasetIndex,
            seriesOption: mouseoverSeries,
            xValue,
            yValue,
            closestTimestamp,
            closestTimestampIndex,
          });
        }
      }
    }
  }

  // Single mode tooltip condition to show series as bold when hovering directly over a line.
  if (maxNearbySeries === 1 && estimatedClosestSeries !== undefined) {
    // Early return for best performance since we have already found the closest series and do not need to loop through raw data.
    return [estimatedClosestSeries];
  }

  // Find the timestamp with data that is closest to cursorX
  for (let seriesIndex = 0; seriesIndex < totalSeries; seriesIndex++) {
    const currentSeries = seriesMapping[seriesIndex];
    const currentDatasetIndex = currentSeries?.datasetIndex;

    if (currentDatasetIndex === undefined) break;

    const currentDataset = dataset[currentDatasetIndex];
    if (!currentDataset) break;

    const currentDatasetValues: TimeSeriesDatasetSource[] = currentDataset.source;
    if (currentDatasetValues === undefined || !Array.isArray(currentDatasetValues)) break;

    if (Array.isArray(dataset)) {
      // Use estimated timestamp index instead of looping through time series values
      const nearbyTimeSeries = currentDatasetValues[closestTimestampIndex];
      if (nearbyTimeSeries === undefined || !Array.isArray(nearbyTimeSeries)) break;

      const [xValue, yValue] = nearbyTimeSeries;

      // Compare cursor position to raw data and populate data to show in SeriesInfo.tsx
      // TODO: look into using context to avoid prop drilling, would allow for single mouse listener
      if (yValue !== undefined && yValue !== null && !(excludeEmptyResults === 'all' && !yValue)) {
        // Does yValue fall within the cursor range for corresponding yAxis (left or right)
        const yAxisIndex = seriesMapping[seriesIndex]?.yAxisIndex || 0;
        const cursor = cursorY[yAxisIndex] || cursorY[0];
        const buffer = yBuffer[yAxisIndex] || yBuffer[0];

        if (cursor === undefined || buffer === undefined) {
          continue;
        }

        // Figure out the distance away from the cursor in buffers rather than a raw distance
        // The reason for this is if one YAxis is a giant scale (say 0->500m) and the other
        // axis is on a small scale (say 1->10) then the cursor could be very close to a series
        // on the giant scale, but still be 1m away, so EVERY series on the 0-10 would be closer by
        // raw value only.
        const rawDistance = Math.abs(yValue - cursor);
        const bufferDistance = rawDistance / (buffer || 0.01); // Just being cautious against divide by 0

        const isSeriesWithinYBuffer = cursor < yValue + buffer && cursor >= yValue - buffer;
        // Check whether series is close to the cursor
        if (isSeriesWithinYBuffer) {
          const currentSeries = seriesMapping[seriesIndex];
          if (!currentSeries) break;

          const seriesInfo = getSeriesInfo({
            seriesIndex,
            seriesOption: currentSeries,
            xValue,
            yValue,
            closestTimestamp,
            closestTimestampIndex,
          });

          if (seriesInfo === null) {
            return EMPTY_TOOLTIP_DATA;
          }

          // Used for finding sorted nearby series
          const distanceFromCursorY = bufferDistance;
          seriesInfo.distanceFromCursorY = distanceFromCursorY;

          // Used to show bold series in the tooltip for closest series.
          seriesInfo.shouldEmphasizeText = currentSeries.id === estimatedClosestSeries?.seriesId;

          // Add the current series to the heap, if it is already at maximum size (maxNearbySeries), check if the
          // current series is closer than the farthest series.
          if (nearbySeriesHeap.size() < maxNearbySeries) {
            nearbySeriesHeap.push(seriesInfo);
          } else {
            const farthestSeries = nearbySeriesHeap.top();
            const isSeriesCloser =
              farthestSeries?.distanceFromCursorY !== undefined &&
              seriesInfo.distanceFromCursorY < farthestSeries.distanceFromCursorY;
            if (isSeriesCloser) {
              nearbySeriesHeap.pop(); // Remove the farthest series
              nearbySeriesHeap.push(seriesInfo); // Add the new, closer series
            } else if (seriesIndex === estimatedClosestSeries?.seriesIndex) {
              // Extra check to ensure series from ECharts mouseover event is actually in nearby tooltip.
              nearbySeriesHeap.pop(); // Remove the farthest series
              nearbySeriesHeap.push(estimatedClosestSeries); // Add mouseover series
            }
          }
        }
      }
    }
  }

  // Returning TooltipSeriesArray is easier to work with in TooltipContent components and keeps MaxHeap
  // usage as an implementation detail of this checkforNearbyTimeSeries function.
  return nearbySeriesHeap.toArray();
}

export interface GetTooltipDataProps {
  tooltipConfig: TooltipConfig;
  maxNearbySeries: number;
  mousePos: CursorData['coords'];
  pinnedPos?: CursorCoordinates;
  dataset: TimeSeriesDataset[];
  seriesMapping: TimeChartSeriesMapping;
  timeScale: TimeScale;
  chartApi?: ChartApi;
  /**
   * Set by mouseover and mouseout events in TimeChartBase.
   * Used to show the closest series as bold in the tooltip.
   */
  mouseoverSeriesId?: string;
  eventSeries?: TimeSeriesOption;
  timeZone?: string;
}

export type TimeSeriesTooltipData = {
  /**
   * Data for series to show in the tooltip based on cursor position.
   */
  tooltipSeries: TooltipSeriesArray;
  /**
   * Precomputed total to show in tooltip footer for stacked graphs.
   */
  total?: number;
};

/**
 * Uses mouse position to determine whether user is hovering over a chart canvas
 * If yes, convert from pixel values to logical cartesian coordinates and return all nearby series
 */
export function getTooltipData(props: GetTooltipDataProps): TimeSeriesTooltipData {
  const {
    tooltipConfig,
    maxNearbySeries,
    pinnedPos,
    dataset,
    seriesMapping,
    chartApi,
    eventSeries,
    timeScale,
    mouseoverSeriesId,
  } = props;

  const {
    excludeEmptyResults,
    rightYAxisIndex = -1,
    getSeriesById,
    hasLogScale,
    isStacked,
    totalValuePerTimestamp,
  } = tooltipConfig;

  let { mousePos } = props;

  if (chartApi === undefined || mousePos === null) return { tooltipSeries: EMPTY_TOOLTIP_DATA };

  // Allows moving cursor inside tooltip without it fading away.
  if (pinnedPos) {
    mousePos = pinnedPos;
  }

  const chartModel = chartApi.getChartModel();
  if (chartModel === undefined || dataset === null) return { tooltipSeries: EMPTY_TOOLTIP_DATA };

  // plotCanvas corresponds to zrX and zrY, which can be undefined when not hovering over a chart canvas.
  if (!mousePos || mousePos.plotCanvas.x === undefined || mousePos.plotCanvas.y === undefined) {
    return { tooltipSeries: EMPTY_TOOLTIP_DATA };
  }

  const pointInGrid = getPointInGrid(mousePos.plotCanvas.x, mousePos.plotCanvas.y, chartApi) ?? [];

  let total: number | undefined = undefined;

  if (pointInGrid !== null) {
    const chartContainsPixel = chartApi.containPixel('grid', [mousePos.plotCanvas.x, mousePos.plotCanvas.y]);
    if (chartContainsPixel) {
      const { closestTimestamp, closestTimestampIndex } = checkDatasetForClosestTimestamp(
        dataset,
        pointInGrid,
        timeScale
      );

      if (totalValuePerTimestamp !== undefined && closestTimestamp) {
        total = totalValuePerTimestamp.get(closestTimestamp);
      }

      if (closestTimestamp === null || closestTimestampIndex === null) {
        return { tooltipSeries: EMPTY_TOOLTIP_DATA };
      }

      const hasAlternateYAxis = rightYAxisIndex >= 0;

      /*
        Use pixel-based proximity when:
        - Log scale or alternate y-axis: value-space comparison doesn't work.
        - Stacked charts: cursor Y is cumulative but dataset has raw per-series values,
          so value-space comparison fails (especially with auto-scaled stacked min).
      */
      if (hasLogScale || hasAlternateYAxis || isStacked) {
        const tooltipSeries = checkForNearbyTimeSeriesByPoints({
          maxNearbySeries,
          dataset,
          seriesMapping,
          yPosition: mousePos.plotCanvas.y || 0,
          chartApi,
          closestTimestamp,
          closestTimestampIndex,
          mouseoverSeriesId,
          getSeriesById,
          excludeEmptyResults,
        });
        return { tooltipSeries, total };
      }

      const cursorY: number | null = pointInGrid[1] ?? null;
      let rightAxisCursorY: number | null = null;
      const hasRightYAxis = rightYAxisIndex >= 0;

      if (hasRightYAxis) {
        const rightYAxisPointInGrid = chartApi.convertFromPixel({ seriesIndex: rightYAxisIndex }, [
          mousePos.plotCanvas.x,
          mousePos.plotCanvas.y,
        ]);

        if (rightYAxisPointInGrid && rightYAxisPointInGrid[1] !== undefined) {
          rightAxisCursorY = rightYAxisPointInGrid[1];
        }
      }

      if (cursorY === null) {
        return { tooltipSeries: EMPTY_TOOLTIP_DATA };
      }

      const ycursors = [cursorY];
      if (rightAxisCursorY !== null) {
        ycursors.push(rightAxisCursorY);
      }

      const tooltipSeries = checkforNearbyTimeSeries({
        maxNearbySeries,
        dataset,
        seriesMapping,
        cursorY: ycursors,
        yBuffer: ycursors.map((_, index) =>
          getYBuffer({ yInterval: chartModel._componentsMap.get('yAxis')[index].axis.scale._interval as number })
        ),
        closestTimestamp,
        closestTimestampIndex,
        mouseoverSeriesId,
        excludeEmptyResults,
        getSeriesById,
      });
      return { tooltipSeries, total };
    }

    const xInterval = chartModel.getComponent('xAxis').axis.scale._interval;

    // check for events if click happens outside of the grid
    return {
      tooltipSeries: checkForNearestEvent({
        eventSeries,
        pointInGrid,
        xInterval,
      }),
    };
  }

  return { tooltipSeries: EMPTY_TOOLTIP_DATA };
}

/*
 * Get vertical range of grid to check within, impacts how many nearby series show in the tooltip.
 * Ensure the interval is high enough that all series will be considered in checkforNearbyTimeSeries.
 */
export function getYBuffer({ yInterval }: { yInterval: number }) {
  // Roughly correlates with entire grid so all of chart canvas is searched.
  return yInterval * 10;
}

/*
 * Helper to lookup all series info
 */
export function getSeriesInfo({
  seriesIndex,
  seriesOption,
  xValue,
  yValue,
  closestTimestamp,
  closestTimestampIndex,
}: {
  seriesIndex: number;
  seriesOption: TimeSeriesOption;
  xValue: number;
  yValue: number;
  closestTimestamp: number;
  closestTimestampIndex: number;
}): TooltipSeriesInfo {
  const currentSeriesName = seriesOption?.formattedSeriesName ?? String(seriesOption.name);
  const markerColor = seriesOption.color ?? '#000';
  return {
    seriesIndex,
    seriesId: String(seriesOption.id),
    dataIndex: closestTimestampIndex,
    seriesName: currentSeriesName,
    date: closestTimestamp,
    x: xValue,
    y: yValue,
    markerColor: markerColor.toString(),
    markerType: seriesOption?.isComparison ? 'striped' : 'solid',
    distanceFromCursorY: 0,
    shouldEmphasizeText: true,
  };
}

export interface CheckForNearbySeriesByPointsProps {
  chartApi: ChartApi;
  closestTimestampIndex: number;
  dataset: TimeSeriesDataset[];
  maxNearbySeries: number;
  yPosition: number;
  seriesMapping: TimeChartSeriesMapping;
  closestTimestamp: number;
  mouseoverSeriesId?: string;
  getSeriesById?: TooltipConfig['getSeriesById'];
  excludeEmptyResults?: ExcludeEmptyResults;
}

/**
 * Checks for nearby series based on the cursor position and the
 * cartesian coordinates of the series data points on the chart
 */
export function checkForNearbyTimeSeriesByPoints(props: CheckForNearbySeriesByPointsProps): TooltipSeriesArray {
  const {
    chartApi,
    seriesMapping,
    dataset,
    closestTimestampIndex,
    maxNearbySeries: adjustedMaxNearbySeries,
    yPosition,
    closestTimestamp,
    mouseoverSeriesId,
    getSeriesById,
    excludeEmptyResults,
  } = props;

  // If ECharts mouseover event in TimeChartBase finds a series, lookup the info to show in tooltip.
  const mouseoverSeriesInfo = getSeriesById && mouseoverSeriesId ? getSeriesById(mouseoverSeriesId) : undefined;
  let mousedOverIndex: number | null = null;

  if (mouseoverSeriesInfo) {
    mousedOverIndex = mouseoverSeriesInfo.mappingIndex;
  }

  const points = new MaxHeap<{
    index: number;
    point: { x: number; y: number };
    distance: number;
  }>((point) => (point.index === mousedOverIndex ? -1 : (point.distance ?? Infinity)));

  for (let seriesMappingIndex = 0; seriesMappingIndex < seriesMapping.length; seriesMappingIndex++) {
    const mapping = seriesMapping[seriesMappingIndex];
    if (mapping && mapping.datasetIndex !== undefined) {
      // The chart.convertToPixel call can end up returning something that is non-iterable
      // which was leading to an error being thrown.  Have not been able to consistently
      // reproduce what causes that, but this will protect against that causing an error to be
      // thrown that bubbles up to the chart.
      try {
        const data = dataset[mapping.datasetIndex];
        if (data) {
          const [x, y] = data.source[closestTimestampIndex] || [0, 0];

          const [_, yValue = null] =
            y === null
              ? [null, null]
              : (chartApi.convertToPixel(
                  {
                    seriesIndex: seriesMappingIndex,
                  },
                  [x, y]
                ) ?? [null, null]);

          const distance = yValue === null ? Infinity : Math.abs(yPosition - yValue);

          if (
            y !== null &&
            y !== LOG_SCALE_AXIS_NEAR_ZERO_NUMBER &&
            !isNaN(y) &&
            !(excludeEmptyResults === 'all' && !y)
          ) {
            const newPoint = {
              index: seriesMappingIndex,
              point: { x, y },
              distance: yValue === null ? Infinity : Math.abs(yPosition - yValue),
            };

            if (points.size() < adjustedMaxNearbySeries) {
              points.push(newPoint);
            } else {
              const farthestPoint = points.top();
              const isPointCloser =
                // If the series is the moused over series, it's always closer
                seriesMappingIndex === mousedOverIndex ||
                // If the series is not the moused over series, it's only closer if it's closer to the cursor
                // than the farthest series in the heap
                (farthestPoint?.distance !== undefined && distance < farthestPoint.distance);

              if (isPointCloser) {
                points.pop(); // Remove the farthest point
                points.push(newPoint); // Add the new, closer point
              }
            }
          }
        }
      } catch (_e) {
        // noop
      }
    }
  }

  const nearbyPoints = points.toArray();

  return nearbyPoints.map((point) => {
    const series = seriesMapping[point.index];
    const seriesInfo = getSeriesInfo({
      seriesIndex: point.index,
      seriesOption: series || {},
      xValue: point.point.x,
      yValue: point.point.y ?? 0,
      closestTimestamp,
      closestTimestampIndex,
    });

    if (point.index !== mousedOverIndex) {
      seriesInfo.shouldEmphasizeText = false;
    }

    return seriesInfo;
  });
}
